import FileDetail from "./FileDetail";
import FileIcon from "./FileIcon";
import PdfPages from "./PdfPages";
import * as display from "./templateDisplay";
import * as lineage from "./templateLineage";
import { brandTypeStyle } from "./templateTypeStyle";
import {
  SaveAltRounded as DownloadIcon,
  UploadFileRounded as UploadIcon,
  RefreshRounded as RefreshIcon,
  SearchRounded as SearchIcon,
  ClearRounded as ClearIcon,
  AddRounded as AddIcon,
  OpenInFullRounded as ExpandIcon,
  EditRounded as EditIcon,
  SellRounded as FormNumberIcon,
  BlockRounded as DeprecateIcon,
  TuneRounded as InputsIcon,
  NotesRounded as OtherIcon,
  ArrowBackRounded as BackIcon,
  ChevronRightRounded as ChevronIcon,
  GestureRounded as AnnotateIcon,
  CloseRounded as CloseIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import DocumentVariableMapper from "components/templateRequests/documentMapper";
import * as fileUtils from "fileUtils";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useConvertFileToPdfMutation,
  useCreateTemplateRequestMutation,
  useListTemplateRequestsQuery,
  useListTemplatesQuery,
} from "services/docGenService";
import { useConfigQuery } from "services/staticDataService";

const PDF_FORMS = ["PDF_ENDORSEMENT", "ENDORSABLE_PDF"];

const STATUS_COLORS = {
  SUBMITTED: "info",
  IN_PROGRESS: "warning",
  IN_REVIEW: "warning",
  COMPLETED: "success",
  CANCELLED: "default",
  NO_CHANGES_REQUIRED: "default",
};

// ---------------------------------------------------------------------------
// Template list row
// ---------------------------------------------------------------------------

const metaChipSx = {
  height: 18,
  "& .MuiChip-label": { px: 0.75, fontSize: "0.65rem" },
};

const TemplateListItem = memo(function TemplateListItem({
  template,
  selected,
  onSelect,
  requestCount,
  endoConfig,
}) {
  const ctx = { template, endoConfig, requestCount };
  const primary = display.formNumberDisplay(template);
  const secondary = display.templateLabel(template);
  const chips = display.buildChips(display.LIST_CHIPS, ctx);

  return (
    <ListItemButton
      id={`tpl-${template.id}`}
      selected={selected}
      onClick={() => onSelect(template.id)}
      sx={{ alignItems: "flex-start", py: 1 }}
    >
      <Stack gap={0.5} sx={{ width: "100%", minWidth: 0 }}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          gap={1}
          sx={{ minWidth: 0 }}
        >
          <FileIcon type={template.form} typeStyle={brandTypeStyle} />
          <Typography
            variant={"body2"}
            fontWeight={600}
            noWrap
            sx={{ flexGrow: 1, minWidth: 0 }}
          >
            {primary}
          </Typography>
        </Stack>

        {secondary && (
          <Typography variant={"caption"} color={"text.secondary"} noWrap>
            {secondary}
          </Typography>
        )}

        {chips.length > 0 && (
          <Stack
            direction={"row"}
            gap={0.5}
            flexWrap={"wrap"}
            alignItems={"center"}
          >
            {chips.map((chip, index) => (
              <Chip
                key={index}
                size={"small"}
                variant={chip.variant ?? "outlined"}
                color={chip.color}
                label={chip.label}
                sx={{ ...metaChipSx, ...chip.sx }}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </ListItemButton>
  );
});

// ---------------------------------------------------------------------------
// PDF preview
// ---------------------------------------------------------------------------

// Best-effort human-readable message from a thrown/rejected value. RTK-query
// errors surface as objects, so String(e) would render "[object Object]".
const errText = (e) => {
  if (!e) return "Something went wrong.";
  if (typeof e === "string") return e;
  return (
    e.data?.message ??
    e.data?.error ??
    e.error ??
    e.message ??
    "Something went wrong."
  );
};

// Prefetches the template's PDF preview. Runs whenever the selected template
// changes — so the (slow, LibreOffice) conversion is kicked off the moment a
// template is selected and is usually warm by the time the user opens the
// Preview tab. Returns the shared {url, loading, error} state.
const useTemplatePreview = (template) => {
  const [convertFileToPdf] = useConvertFileToPdfMutation();
  const [state, setState] = useState({
    url: null,
    loading: false,
    error: null,
  });
  const requestId = useRef(0);
  // Blob URL for the PDF-form path — kept so we can revoke it.
  const objectUrlRef = useRef(null);

  const fileId = template?.fileId;
  const isPdfForm = PDF_FORMS.includes(template?.form);

  const revokeObjectUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  const load = useCallback(async () => {
    const myId = ++requestId.current;
    revokeObjectUrl();
    // Ignore results from a superseded request (user moved on to another
    // template before this conversion finished).
    const apply = (next) => {
      if (requestId.current === myId) {
        setState(next);
      }
    };

    if (!fileId) {
      apply({ url: null, loading: false, error: null });
      return;
    }
    apply({ url: null, loading: true, error: null });
    try {
      let url;
      if (isPdfForm) {
        // Already a PDF — serve it directly rather than round-tripping it
        // through LibreOffice. The files download URL carries an attachment
        // disposition, so embedding it would TRIGGER A DOWNLOAD; fetch the
        // bytes and embed a blob URL instead, which renders inline.
        const directUrl = await fileUtils.getDownloadUrl(fileId);
        const resp = await fetch(directUrl);
        // Force the blob's MIME type to application/pdf — the files bucket may
        // serve it as octet-stream/attachment, which makes <embed> download
        // instead of rendering inline.
        const buffer = await resp.arrayBuffer();
        const blob = new Blob([buffer], { type: "application/pdf" });
        const objectUrl = URL.createObjectURL(blob);
        if (requestId.current === myId) {
          objectUrlRef.current = objectUrl;
        } else {
          URL.revokeObjectURL(objectUrl);
        }
        url = objectUrl;
      } else {
        // Raw template -> PDF via LibreOffice, WITHOUT running jinja, so the
        // {{ placeholders }} stay visible. Cached per file_id server-side.
        const res = await convertFileToPdf({ fileId }).unwrap();
        url = res.pdfDownloadUrl;
      }
      apply({ url, loading: false, error: null });
    } catch (e) {
      apply({ url: null, loading: false, error: errText(e) });
    }
  }, [fileId, isPdfForm, convertFileToPdf]);

  useEffect(() => {
    load();
    return () => revokeObjectUrl();
  }, [load]);

  return { ...state, hasFile: Boolean(fileId), reload: load };
};

const PreviewStatus = ({ preview, sx }) => {
  if (!preview.hasFile) {
    return (
      <Stack alignItems={"center"} justifyContent={"center"} sx={sx}>
        <Typography color={"text.secondary"}>
          {"No document to preview."}
        </Typography>
      </Stack>
    );
  }
  if (preview.loading) {
    return (
      <Stack alignItems={"center"} justifyContent={"center"} gap={2} sx={sx}>
        <CircularProgress size={32} />
        <Typography color={"text.secondary"}>
          {"Generating preview…"}
        </Typography>
      </Stack>
    );
  }
  if (preview.error) {
    return (
      <Stack alignItems={"center"} justifyContent={"center"} gap={2} sx={sx}>
        <Typography
          color={"error"}
        >{`Could not generate preview: ${preview.error}`}</Typography>
        <Button startIcon={<RefreshIcon />} onClick={preview.reload}>
          {"Retry"}
        </Button>
      </Stack>
    );
  }
  return null;
};

// Inline preview — pages are rendered to canvases on a WHITE background via
// pdf.js (PdfPages), because an embedded native PDF viewer has a dark, unstylable
// chrome. The pages flow inside the detail panel's own scroll container (no
// nested scrollbar). The header's expand button opens the full-size dialog
// (which has the native toolbar for zoom / print / download).
const PreviewPanel = ({ preview, onEnlarge }) => {
  const ready =
    !preview.loading && !preview.error && preview.hasFile && preview.url;
  return (
    <Stack gap={1}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={1}
      >
        <Typography variant={"subtitle2"}>{"Preview"}</Typography>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <Typography variant={"caption"} color={"text.secondary"}>
            {"Variables show as placeholders."}
          </Typography>
          {ready && (
            <IconButton
              icon={ExpandIcon}
              variant={"bright"}
              tooltip={"Open full view"}
              onClick={onEnlarge}
            />
          )}
        </Stack>
      </Stack>
      <Divider />
      {ready ? (
        <Paper variant={"outlined"} sx={{ bgcolor: "#fff", p: 1.5 }}>
          <PdfPages url={preview.url} />
        </Paper>
      ) : (
        <PreviewStatus preview={preview} sx={{ height: "40vh", p: 2 }} />
      )}
    </Stack>
  );
};

const PreviewDialog = ({ open, onClose, preview, title }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth={"lg"}>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent sx={{ height: "82vh", p: 0 }}>
      {preview.url ? (
        <Box
          component={"embed"}
          src={preview.url}
          type={"application/pdf"}
          sx={{ width: "100%", height: "100%", border: "none" }}
        />
      ) : (
        <PreviewStatus preview={preview} sx={{ height: "100%" }} />
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>{"Close"}</Button>
    </DialogActions>
  </Dialog>
);

// ---------------------------------------------------------------------------
// Details (overview) — the fast default panel, shown while the preview warms
// ---------------------------------------------------------------------------

const Fact = ({ label, value, muted }) => (
  <Stack direction={"row"} gap={1} alignItems={"baseline"}>
    <Typography
      variant={"caption"}
      color={"text.secondary"}
      sx={{ minWidth: 150, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography
      variant={"body2"}
      color={muted ? "text.secondary" : "text.primary"}
      sx={{ wordBreak: "break-word" }}
    >
      {value}
    </Typography>
  </Stack>
);

const DetailsSection = ({ template, endoConfig, requestCount }) => {
  const ctx = { template, endoConfig, requestCount };
  const facts = display.buildFacts(display.TEMPLATE_FACTS, ctx);
  const attributes = Object.entries(template.attributes ?? {});

  return (
    <Box>
      <Stack gap={2}>
        <Stack gap={0.75}>
          {facts.map((fact, index) => (
            <Fact
              key={index}
              label={fact.label}
              value={fact.value}
              muted={fact.muted}
            />
          ))}
        </Stack>

        {attributes.length > 0 && (
          <>
            <Divider />
            <Stack gap={1}>
              <Typography variant={"subtitle2"}>{"Attributes"}</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {attributes.map(([key, value], index) => (
                  <Chip
                    key={index}
                    size={"small"}
                    variant={"outlined"}
                    label={`${key}: ${value}`}
                  />
                ))}
              </Box>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Endorsement settings — read-only surface of the endorsement config
// ---------------------------------------------------------------------------

const EndorsementSettingsSection = ({ endoConfig }) => {
  const facts = display.buildFacts(display.ENDORSEMENT_FACTS, { endoConfig });
  const inputs = endoConfig?.extraInputs ?? [];

  return (
    <Box>
      <Stack gap={2}>
        <Stack gap={0.75}>
          {facts.map((fact, index) => (
            <Fact
              key={index}
              label={fact.label}
              value={fact.value}
              muted={fact.muted}
            />
          ))}
        </Stack>

        <Divider />
        <Stack gap={1}>
          <Typography variant={"subtitle2"}>{"Inputs"}</Typography>
          {inputs.length === 0 ? (
            <Typography variant={"body2"} color={"text.secondary"}>
              {"No additional inputs are configured for this endorsement."}
            </Typography>
          ) : (
            <Stack gap={1}>
              {inputs.map((input, index) => (
                <Paper key={index} variant={"outlined"} sx={{ p: 1.25 }}>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    gap={1}
                    justifyContent={"space-between"}
                  >
                    <Typography variant={"body2"}>
                      {display.endorsementInputLabel(input)}
                    </Typography>
                    {input?.type && (
                      <Chip
                        size={"small"}
                        variant={"outlined"}
                        label={display.humanize(input.type)}
                      />
                    )}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Linked requests
// ---------------------------------------------------------------------------

const RequestsSection = ({ template, requests }) => {
  // Exact linkage only (by template id); we don't fuzzy-match on a form number.
  const linked = useMemo(
    () =>
      (Array.isArray(requests) ? requests : []).filter(
        (r) => lineage.requestTemplateId(r) === template.id
      ),
    [requests, template.id]
  );

  if (linked.length === 0) {
    return (
      <Typography variant={"body2"} color={"text.secondary"}>
        {"No template requests linked to this template yet."}
      </Typography>
    );
  }

  return (
    <Stack gap={1}>
      {linked.map((request) => (
        <Paper key={request.id} variant={"outlined"} sx={{ p: 1.5 }}>
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <Typography variant={"body2"} sx={{ flexGrow: 1 }}>
              {request.title || request.templateName || request.id}
            </Typography>
            <Chip
              size={"small"}
              variant={"outlined"}
              label={display.humanize(request.type)}
            />
            <Chip
              size={"small"}
              label={display.humanize(request.status)}
              color={STATUS_COLORS[request.status] ?? "default"}
            />
          </Stack>
          <Typography variant={"caption"} color={"text.secondary"}>
            {`${request.id}${
              request.submittedBy ? ` · ${request.submittedBy}` : ""
            }`}
          </Typography>
        </Paper>
      ))}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

// Changes to a template are never applied directly here — they are raised as
// template requests for the doc-gen team to action. This dialog opens
// pre-filled from a "control" preset and (optionally) uploads a new version of
// the document, then creates an EDIT_TEMPLATE request linked to the template.
// The small set of requests operators principally raise about a template.
// There is no formal "change reason" in the backend — every preset becomes an
// EDIT_TEMPLATE request linked to the template, pre-filled with a sensible
// title/description (and an upload where one is needed). `requestType` lets a
// preset (e.g. brand-new edition) be NEW_TEMPLATE if ever needed.
const REQUEST_PRESETS = [
  {
    key: "replacement",
    label: "New edition / replacement",
    help:
      "Upload an updated document to supersede and deprecate the current edition.",
    icon: UploadIcon,
    requireUpload: true,
    makeTitle: (f) => `New edition of ${f}`,
    makeDescription: (f) =>
      `Please publish the attached document as the new edition of ${f} and deprecate the current one.`,
  },
  {
    key: "wording",
    label: "Fix wording / content",
    help: "Correct text, clauses or formatting in the existing template.",
    icon: EditIcon,
    requireUpload: false,
    makeTitle: (f) => `Fix wording in ${f}`,
    makeDescription: (f) =>
      `Please correct the following wording/content in ${f}:\n\n- `,
  },
  {
    key: "metadata",
    label: "Update form number / edition",
    help: "Correct the form number, edition date or naming.",
    icon: FormNumberIcon,
    requireUpload: false,
    makeTitle: (f) => `Update form number / edition for ${f}`,
    makeDescription: (f) =>
      `Please update the form number / edition for ${f}.\n\nCurrent: ${f}\nShould be: `,
  },
  {
    key: "deprecate",
    label: "Deprecate / retire",
    help: "Stop this template being selectable. No replacement.",
    icon: DeprecateIcon,
    requireUpload: false,
    makeTitle: (f) => `Deprecate ${f}`,
    makeDescription: (f) =>
      `Please deprecate ${f} — it should no longer be selectable.\n\nReason: `,
  },
  {
    key: "inputs",
    label: "Change endorsement inputs",
    help: "Add, remove or edit the configurable inputs on this endorsement.",
    icon: InputsIcon,
    requireUpload: false,
    endorsementOnly: true,
    makeTitle: (f) => `Change inputs on ${f}`,
    makeDescription: (f) =>
      `Please change the configurable inputs for ${f}:\n\n- `,
  },
  {
    key: "other",
    label: "Other change",
    help: "Describe any other change to this template.",
    icon: OtherIcon,
    requireUpload: false,
    makeTitle: (f) => `Change request: ${f}`,
    makeDescription: (f) => `Requested change to ${f}:\n\n- `,
  },
];

const CreateRequestDialog = ({
  open,
  onClose,
  template,
  endoConfig,
  onAnnotate,
  onCreated,
}) => {
  const [createTemplateRequest] = useCreateTemplateRequestMutation();
  const [chosen, setChosen] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [createdId, setCreatedId] = useState(null);
  const fileInputRef = useRef(null);

  const formNumber =
    endoConfig?.formNumber || display.formNumberDisplay(template);
  const isEndorsement = endoConfig != null;
  const presets = REQUEST_PRESETS.filter(
    (p) => !p.endorsementOnly || isEndorsement
  );

  // Reset to the picker each time the dialog opens.
  useEffect(() => {
    if (open) {
      setChosen(null);
      setFile(null);
      setError(null);
      setSubmitting(false);
      setCreatedId(null);
    }
  }, [open]);

  const choosePreset = (preset) => {
    setChosen(preset);
    setTitle(preset.makeTitle(formNumber));
    setDescription(preset.makeDescription(formNumber));
    setPriority("MEDIUM");
    setFile(null);
    setError(null);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      let s3FileId;
      let fileType;
      if (file) {
        // Upload the attachment so the request can reference it.
        s3FileId = await fileUtils.createFileEntry(file, false);
        fileType = file.type || null;
      }
      const res = await createTemplateRequest({
        requestType: chosen?.requestType ?? "EDIT_TEMPLATE",
        templateId: template.id,
        formNumber,
        templateName: template.title,
        title,
        description,
        priority,
        status: "SUBMITTED",
        s3FileId,
        fileType,
      }).unwrap();
      setCreatedId(res.id);
      onCreated?.();
    } catch (e) {
      setError(errText(e));
    } finally {
      setSubmitting(false);
    }
  };

  const requireUpload = chosen?.requireUpload;

  const dialogTitle = createdId
    ? "Request created"
    : chosen
    ? chosen.label
    : "New request";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          {chosen && !createdId && (
            <IconButton
              icon={BackIcon}
              variant={"bright"}
              tooltip={"Back"}
              onClick={() => setChosen(null)}
            />
          )}
          {dialogTitle}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {createdId ? (
          <Stack gap={1} sx={{ mt: 1 }}>
            <Typography>{`Request ${createdId} created.`}</Typography>
            <Typography variant={"caption"} color={"text.secondary"}>
              {"It now appears under Linked requests for this template."}
            </Typography>
          </Stack>
        ) : !chosen ? (
          <Stack gap={1} sx={{ mt: 1 }}>
            <Typography variant={"caption"} color={"text.secondary"}>
              {`Raise a request linked to ${formNumber}. Pick what you need:`}
            </Typography>
            <List disablePadding>
              {presets.map((preset) => {
                const Icon = preset.icon;
                return (
                  <ListItemButton
                    key={preset.key}
                    onClick={() => choosePreset(preset)}
                    sx={{ borderRadius: 1, alignItems: "flex-start", py: 1 }}
                  >
                    <Stack
                      direction={"row"}
                      gap={1.5}
                      alignItems={"center"}
                      sx={{ width: "100%" }}
                    >
                      <Icon color={"action"} />
                      <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant={"body2"} fontWeight={600}>
                          {preset.label}
                        </Typography>
                        <Typography
                          variant={"caption"}
                          color={"text.secondary"}
                        >
                          {preset.help}
                        </Typography>
                      </Stack>
                      <ChevronIcon color={"disabled"} />
                    </Stack>
                  </ListItemButton>
                );
              })}
            </List>
          </Stack>
        ) : (
          <Stack gap={2} sx={{ mt: 1 }}>
            <Typography variant={"caption"} color={"text.secondary"}>
              {`Linked to ${formNumber}`}
            </Typography>
            <TextField
              label={"Title"}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              variant={"filled"}
              fullWidth
            />
            <TextField
              label={"Description"}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              variant={"filled"}
              fullWidth
              multiline
              minRows={4}
            />
            <TextField
              select
              label={"Priority"}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              variant={"filled"}
            >
              {["LOW", "MEDIUM", "HIGH"].map((p) => (
                <MenuItem key={p} value={p}>
                  {display.humanize(p)}
                </MenuItem>
              ))}
            </TextField>
            <Box>
              <input
                type={"file"}
                hidden
                ref={fileInputRef}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              <Button
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                {file
                  ? "Change file"
                  : requireUpload
                  ? "Attach new version"
                  : "Attach a file (optional)"}
              </Button>
              {file && (
                <Typography variant={"caption"} sx={{ ml: 1 }}>
                  {file.name}
                </Typography>
              )}
            </Box>
            {requireUpload && !file && (
              <Typography variant={"caption"} color={"text.secondary"}>
                {"A document is required for this request."}
              </Typography>
            )}
            {error && <Alert severity={"error"}>{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{createdId ? "Close" : "Cancel"}</Button>
        {chosen && !createdId && (
          <>
            <Button
              startIcon={<AnnotateIcon />}
              onClick={() => onAnnotate?.({ title, description, priority })}
            >
              {"Annotate document…"}
            </Button>
            <Button
              isLoading={submitting}
              isDisabled={title.trim().length === 0 || (requireUpload && !file)}
              onClick={submit}
            >
              {"Create request"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Full annotation/field-placement editor (reuses the template-request mapper).
// For an existing template it is seeded with that template's document (which
// the user can keep or replace with an edited version); for a new template it
// starts on the upload step. On submit it creates a template request — an
// EDIT_TEMPLATE linked to the template, or a NEW_TEMPLATE — carrying any
// annotations/placements.
const RequestMapperDialog = ({
  open,
  onClose,
  requestType,
  template = null,
  formNumber,
  dialogHeading,
  initialMetadata,
  onCreated,
}) => {
  const [convertFileToPdf] = useConvertFileToPdfMutation();
  const [createTemplateRequest] = useCreateTemplateRequestMutation();
  const [error, setError] = useState(null);
  const [seedFile, setSeedFile] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);

  const fileId = template?.fileId;

  // The mapper renders from a browser File, not just an s3FileId, so for an
  // existing template we fetch its document into a File first.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setError(null);
    setSeedFile(null);
    if (!fileId) {
      return undefined;
    }
    setLoadingFile(true);
    (async () => {
      try {
        const url = await fileUtils.getDownloadUrl(fileId);
        const resp = await fetch(url);
        const blob = await resp.blob();
        const name = template?.filename || "template.docx";
        if (!cancelled)
          setSeedFile(new File([blob], name, { type: blob.type }));
      } catch (e) {
        if (!cancelled) setError(errText(e));
      } finally {
        if (!cancelled) setLoadingFile(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Depend on primitives, not the `template` object identity (which changes
    // whenever the templates query re-emits), so the seed file isn't re-fetched
    // mid-session when an unrelated refetch fires.
  }, [open, fileId, template?.filename]);

  const handleSubmit = async (requestData) => {
    setError(null);
    try {
      const res = await createTemplateRequest({
        requestType,
        templateId: template?.id,
        formNumber: template
          ? formNumber || display.formNumberDisplay(template)
          : undefined,
        templateName: template?.title || requestData.fileName,
        title: requestData.title,
        description: requestData.description,
        priority: requestData.priority || "MEDIUM",
        status: "SUBMITTED",
        selectedFields: (requestData.placements ?? [])
          .map((p) => p.variable?.path)
          .filter(Boolean),
        customFields: requestData.customFields ?? [],
        placements: requestData.placements ?? [],
        annotations: requestData.annotations ?? [],
        s3FileId: requestData.s3FileId,
        fileType: requestData.fileType,
      }).unwrap();
      onCreated?.(res.id);
      onClose();
    } catch (e) {
      setError(errText(e));
    }
  };

  // Ready to render the mapper: a new-template flow needs no seed; an existing
  // template waits for its document to load.
  const ready = !template || (seedFile != null && !loadingFile);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={"xl"}
      PaperProps={{ sx: { height: "92vh" } }}
    >
      <DialogTitle>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={1}
        >
          <span>{dialogHeading}</span>
          <IconButton
            icon={CloseIcon}
            variant={"bright"}
            tooltip={"Close"}
            onClick={onClose}
          />
        </Stack>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ p: 0, display: "flex", flexDirection: "column" }}
      >
        {error && (
          <Alert severity={"error"} sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {open && loadingFile && (
          <Stack alignItems={"center"} justifyContent={"center"} sx={{ py: 6 }}>
            <CircularProgress size={32} />
            <Typography color={"text.secondary"} sx={{ mt: 2 }}>
              {"Loading document…"}
            </Typography>
          </Stack>
        )}
        {open && ready && (
          <Box sx={{ flexGrow: 1, minHeight: 0 }}>
            <DocumentVariableMapper
              requestType={requestType}
              initialFile={
                template
                  ? { file: seedFile, s3FileId: template.fileId }
                  : undefined
              }
              initialMetadata={initialMetadata}
              useServerSidePdfPreview={true}
              convertFileToPdfFn={convertFileToPdf}
              originalS3FileId={template?.fileId ?? null}
              originalFileName={template?.filename ?? null}
              submitLabel={"Create request"}
              onSubmitRequest={handleSubmit}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, action, children }) => (
  <Stack gap={1}>
    <Stack
      direction={"row"}
      alignItems={"center"}
      justifyContent={"space-between"}
      gap={1}
    >
      <Typography variant={"subtitle2"}>{title}</Typography>
      {action}
    </Stack>
    <Divider />
    <Box>{children}</Box>
  </Stack>
);

// ---------------------------------------------------------------------------
// Detail panel — single consolidated view: info on the left, preview on the
// right (click to enlarge). No tabs.
// ---------------------------------------------------------------------------

const TemplateDetail = ({
  template,
  requests,
  endoConfig,
  requestCount,
  onRequestsChanged,
}) => {
  const preview = useTemplatePreview(template);
  const [enlarged, setEnlarged] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [annotateMeta, setAnnotateMeta] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const ctx = { template, endoConfig, requestCount };
  const heading = display.formNumberDisplay(template);
  const subtitle = display.templateLabel(template);
  const headerChips = display.buildChips(display.DETAIL_CHIPS, ctx);
  const isEndorsement = endoConfig != null;
  const formNumber =
    endoConfig?.formNumber || display.formNumberDisplay(template);

  const download = async () => {
    setDownloading(true);
    try {
      await fileUtils.saveFile(template.fileId, template.filename);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Paper variant={"outlined"} sx={{ height: "100%" }}>
      <Box sx={{ p: 2 }}>
        <FileDetail
          name={heading}
          filename={subtitle ?? template.filename}
          type={template.form}
          typeStyle={brandTypeStyle}
        />
        <Stack direction={"row"} gap={1} sx={{ mt: 1 }} flexWrap={"wrap"}>
          {headerChips.map((chip, index) => (
            <Chip
              key={index}
              size={"small"}
              color={chip.color}
              variant={chip.variant ?? "outlined"}
              label={chip.label}
              sx={chip.sx}
            />
          ))}
        </Stack>
      </Box>
      <Divider />

      <Box sx={{ p: 2, maxHeight: "80vh", overflow: "auto" }}>
        <Stack gap={3}>
          <Stack direction={"row"} gap={1} flexWrap={"wrap"}>
            <Button
              size={"small"}
              startIcon={<AddIcon />}
              onClick={() => setRequestOpen(true)}
            >
              {"New request"}
            </Button>
            <Button
              size={"small"}
              startIcon={<DownloadIcon />}
              isLoading={downloading}
              isDisabled={!template.fileId}
              onClick={download}
            >
              {"Download original"}
            </Button>
          </Stack>

          <Section title={"Details"}>
            <DetailsSection
              template={template}
              endoConfig={endoConfig}
              requestCount={requestCount}
            />
          </Section>

          {isEndorsement && (
            <Section title={"Endorsement settings"}>
              <EndorsementSettingsSection endoConfig={endoConfig} />
            </Section>
          )}

          <Section
            title={"Linked requests"}
            action={
              <Button
                size={"small"}
                startIcon={<AddIcon />}
                onClick={() => setRequestOpen(true)}
              >
                {"New request"}
              </Button>
            }
          >
            <RequestsSection template={template} requests={requests} />
          </Section>

          <PreviewPanel preview={preview} onEnlarge={() => setEnlarged(true)} />
        </Stack>
      </Box>

      <CreateRequestDialog
        open={requestOpen}
        template={template}
        endoConfig={endoConfig}
        onClose={() => setRequestOpen(false)}
        onCreated={onRequestsChanged}
        onAnnotate={(metadata) => {
          setRequestOpen(false);
          setAnnotateMeta(metadata);
        }}
      />
      <RequestMapperDialog
        open={annotateMeta != null}
        requestType={"EDIT_TEMPLATE"}
        template={template}
        formNumber={formNumber}
        dialogHeading={`Annotate & request — ${heading}`}
        initialMetadata={annotateMeta}
        onClose={() => setAnnotateMeta(null)}
        onCreated={onRequestsChanged}
      />
      <PreviewDialog
        open={enlarged}
        onClose={() => setEnlarged(false)}
        preview={preview}
        title={heading}
      />
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const TemplateAdmin = () => {
  // Runs on the current user's own company via a normal token — no company
  // path param, no impersonation. templateId (from /templates/:templateId)
  // drives selection so a specific template can be linked to.
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  // Debounced copy used for filtering, so typing in the (400+ row) list doesn't
  // re-filter and re-render on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 150);
    return () => clearTimeout(t);
  }, [search]);
  const [formFilter, setFormFilter] = useState("");
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const searchRef = useRef(null);
  const filterRef = useRef(null);
  const listRef = useRef(null);
  const selectedId = templateId ?? null;
  const selectTemplate = useCallback(
    (id) => {
      navigate(`/templates/${id}`);
      // Keep the selected row visible when navigating via the keyboard.
      document
        .getElementById(`tpl-${id}`)
        ?.scrollIntoView({ block: "nearest" });
    },
    [navigate]
  );

  const {
    data: templates,
    isFetching,
    refetch: refetchTemplates,
  } = useListTemplatesQuery();
  const {
    data: requestsData,
    refetch: refetchRequests,
  } = useListTemplateRequestsQuery({ includeAll: true });
  const { data: endorsementsData } = useConfigQuery("endorsements");

  // Endorsement config, keyed by endorsement key (== the template title).
  const endorsementItems = useMemo(() => endorsementsData?.items ?? {}, [
    endorsementsData,
  ]);

  // The endpoint returns { requests: [...] }; normalise to a flat array.
  const requests = useMemo(
    () =>
      Array.isArray(requestsData?.requests)
        ? requestsData.requests
        : Array.isArray(requestsData)
        ? requestsData
        : [],
    [requestsData]
  );

  const refresh = useCallback(() => {
    refetchTemplates();
    refetchRequests();
  }, [refetchTemplates, refetchRequests]);

  // list-templates returns the TemplateResponse shape (snake_case, and without
  // variables/active/file-less templates). Normalise the few fields our UI
  // reads to the camelCase shape the components expect.
  const templatesList = useMemo(
    () =>
      (Array.isArray(templates) ? templates : []).map((t) => ({
        ...t,
        fileId: t.fileId ?? t.file_id ?? null,
        includedEndorsements:
          t.includedEndorsements ?? t.included_endorsements ?? null,
        includedEndorsementsMode:
          t.includedEndorsementsMode ?? t.included_endorsements_mode ?? null,
        policyForms: t.policyForms ?? t.policy_forms ?? null,
        allowedEmailTargets:
          t.allowedEmailTargets ?? t.allowed_email_targets ?? null,
      })),
    [templates]
  );

  // Count of template requests linked to each template, so the list can show
  // "N requests" at a glance.
  const requestCountById = useMemo(() => {
    const counts = new Map();
    for (const request of Array.isArray(requests) ? requests : []) {
      const id = lineage.requestTemplateId(request);
      if (id) {
        counts.set(id, (counts.get(id) ?? 0) + 1);
      }
    }
    return counts;
  }, [requests]);

  // Type filter: only groups that actually have members are offered.
  const facetGroups = useMemo(() => {
    const present = new Set(templatesList.map((t) => t.form));
    return Object.entries(display.TYPE_GROUPS)
      .map(([label, values]) => ({ label, values }))
      .filter((g) => g.values.some((v) => present.has(v)));
  }, [templatesList]);

  const selectedGroupValues = useMemo(() => {
    const group = facetGroups.find((g) => g.label === formFilter);
    return group ? new Set(group.values) : null;
  }, [facetGroups, formFilter]);

  // Flat list, filtered by the search box and the type group, then sorted by
  // template title.
  const filteredTemplates = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();

    const matches = templatesList.filter((t) => {
      if (selectedGroupValues && !selectedGroupValues.has(t.form)) return false;
      if (!term) return true;
      return display.SEARCH_FIELDS.some((field) => {
        const v = field(t);
        return v != null && String(v).toLowerCase().includes(term);
      });
    });

    return [...matches].sort((a, b) =>
      String(a.title ?? "").localeCompare(String(b.title ?? ""))
    );
  }, [templatesList, debouncedSearch, selectedGroupValues]);

  const selectedTemplate = useMemo(
    () => templatesList.find((t) => t.id === selectedId) ?? null,
    [templatesList, selectedId]
  );

  // Up/Down walk a single focus chain: search field → type filter → list items
  // (and back up). Handled in the capture phase so it wins over the MUI Select's
  // own arrow handling; scoped to those three zones so we never hijack arrow
  // keys / scrolling elsewhere on the page.
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      // Let native handling win while a dialog or an open dropdown is up.
      if (
        document.querySelector(
          '[role="dialog"], [role="listbox"], .MuiPopover-root'
        )
      )
        return;

      const el = document.activeElement;
      const inSearch = el === searchRef.current;
      const inFilter = Boolean(el && filterRef.current?.contains(el));
      const inList = Boolean(el && listRef.current?.contains(el));
      if (!inSearch && !inFilter && !inList) return;

      const up = e.key === "ArrowUp";
      const hasFilter = Boolean(filterRef.current);
      e.preventDefault();
      e.stopPropagation();

      const focusSearch = () => {
        searchRef.current?.focus();
        searchRef.current?.select?.();
      };
      const focusFilter = () =>
        filterRef.current?.querySelector('[role="combobox"]')?.focus();
      const goToList = (idx) => {
        const target = filteredTemplates[idx];
        if (!target) return;
        selectTemplate(target.id);
        document.getElementById(`tpl-${target.id}`)?.focus();
      };

      if (inSearch) {
        if (up) return; // top of the chain
        if (hasFilter) focusFilter();
        else goToList(0);
        return;
      }
      if (inFilter) {
        if (up) focusSearch();
        else goToList(0);
        return;
      }
      // inList
      const idx = filteredTemplates.findIndex((t) => t.id === selectedId);
      if (up) {
        if (idx <= 0) {
          if (hasFilter) focusFilter();
          else focusSearch();
          return;
        }
        goToList(idx - 1);
      } else {
        goToList(idx < 0 ? 0 : Math.min(idx + 1, filteredTemplates.length - 1));
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [filteredTemplates, selectedId, selectTemplate]);

  return (
    <Component
      title={"Templates"}
      isFetching={isFetching}
      options={
        <Stack direction={"row"} gap={1} alignItems={"center"}>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setNewTemplateOpen(true)}
          >
            {"New Template"}
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={refresh}>
            {"Refresh"}
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={2} sx={{ p: 1 }}>
        <Grid item xs={12} md={4}>
          <Stack gap={1}>
            <TextField
              inputRef={searchRef}
              autoFocus
              fullWidth
              size={"small"}
              placeholder={"Filter templates…"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position={"start"}>
                    <SearchIcon fontSize={"small"} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position={"end"}>
                    <IconButton
                      icon={ClearIcon}
                      variant={"bright"}
                      tooltip={"Clear filter"}
                      onClick={() => setSearch("")}
                    />
                  </InputAdornment>
                ) : null,
              }}
            />
            {facetGroups.length > 0 && (
              <TextField
                select
                fullWidth
                ref={filterRef}
                size={"small"}
                label={"Type"}
                value={formFilter}
                onChange={(e) => setFormFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value={""}>{"All"}</MenuItem>
                {facetGroups.map((group) => (
                  <MenuItem key={group.label} value={group.label}>
                    {group.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              sx={{ px: 0.5 }}
            >
              {search || formFilter
                ? `Showing ${filteredTemplates.length} of ${templatesList.length}`
                : `${templatesList.length} templates`}
            </Typography>
            <Paper
              ref={listRef}
              variant={"outlined"}
              sx={{ maxHeight: "72vh", overflow: "auto" }}
            >
              <List dense disablePadding>
                {filteredTemplates.map((template) => (
                  <TemplateListItem
                    key={template.id}
                    template={template}
                    selected={template.id === selectedId}
                    onSelect={selectTemplate}
                    requestCount={requestCountById.get(template.id) ?? 0}
                    endoConfig={endorsementItems[template.title]}
                  />
                ))}
                {filteredTemplates.length === 0 && (
                  <Typography color={"text.secondary"} sx={{ p: 2 }}>
                    {"No templates found."}
                  </Typography>
                )}
              </List>
            </Paper>
          </Stack>
        </Grid>
        <Grid item xs={12} md={8}>
          {selectedTemplate ? (
            <TemplateDetail
              template={selectedTemplate}
              requests={requests}
              endoConfig={endorsementItems[selectedTemplate.title]}
              requestCount={requestCountById.get(selectedTemplate.id) ?? 0}
              onRequestsChanged={refetchRequests}
            />
          ) : (
            <Stack
              alignItems={"center"}
              justifyContent={"center"}
              sx={{ height: "40vh" }}
            >
              <Typography color={"text.secondary"}>
                {"Select a template to view its details, requests and preview."}
              </Typography>
            </Stack>
          )}
        </Grid>
      </Grid>

      <RequestMapperDialog
        open={newTemplateOpen}
        requestType={"NEW_TEMPLATE"}
        dialogHeading={"New template request"}
        initialMetadata={{
          title: "New template",
          description: "Please add a new template.\n\n- ",
          priority: "MEDIUM",
        }}
        onClose={() => setNewTemplateOpen(false)}
        onCreated={refetchRequests}
      />
    </Component>
  );
};

export default TemplateAdmin;
